import React, { useEffect, useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MenuCard from './MenuCard';
import { MenuList } from './data';

/**
 * @component
 * @description Main sidebar navigation component for the Devotee CMS admin panel.
 * Provides a comprehensive navigation menu with permission-based access control,
 * dynamic menu filtering, and responsive design.
 *
 * Key Features:
 * - **Permission-based Navigation**: Dynamically shows/hides menu items based on user permissions
 * - **Role-based Access Control**: Special handling for admin vs sub-admin roles
 * - **Dynamic Menu Processing**: Processes menu list with permission checks in real-time
 * - **Active Menu Tracking**: Tracks and highlights currently active submenu
 * - **Responsive Design**: Sticky positioning with proper overflow handling
 * - **Professional Styling**: Dark theme with consistent color scheme
 * - **Performance Optimization**: Memoized menu processing to prevent unnecessary re-renders
 * - **Route Integration**: Seamless integration with React Router for navigation
 *
 * Permission Logic:
 * - **Module Permissions**: Checks user permissions for each menu module
 * - **View Access**: Only shows menus where user has view permissions
 * - **Admin Restrictions**: Hides admin-only sections from sub-admin users
 * - **Default Visibility**: Defaults to showing menus if no specific permission is defined
 *
 * Menu Processing:
 * - **Permission Mapping**: Maps user permissions to menu items
 * - **Visibility Calculation**: Determines which menus should be visible
 * - **Filtering**: Removes non-visible menus from the rendered list
 * - **Memoization**: Optimizes performance by caching processed menu list
 *
 * Styling Features:
 * - **Dark Theme**: Professional dark blue background (#002047)
 * - **Sticky Positioning**: Remains fixed during page scrolling
 * - **Proper Dimensions**: Full height with responsive width (max 280px)
 * - **Overflow Handling**: Vertical scrolling for long menu lists
 * - **Top Offset**: Positioned below the top navigation bar (64px)
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the sidebar is in open/expanded state
 *
 * @returns {JSX.Element} Complete sidebar navigation with permission-based menu items
 *
 * @example
 * // Basic usage in main layout
 * <Sidebar open={sidebarOpen} />
 *
 * @example
 * // Usage with responsive sidebar state
 * const [sidebarOpen, setSidebarOpen] = useState(true);
 *
 * <Sidebar open={sidebarOpen} />
 *
 * @example
 * // Integration in main admin layout
 * function AdminLayout() {
 *   const [drawerOpen, setDrawerOpen] = useState(false);
 *
 *   return (
 *     <Box sx={{ display: 'flex' }}>
 *       <Sidebar open={drawerOpen} />
 *       <MainContent />
 *     </Box>
 *   );
 * }
 *
 * @see MenuCard - Individual menu item component
 * @see MenuList - Menu configuration data
 * @see useSelector - Redux hook for accessing admin data
 * @see useNavigate - React Router hook for navigation
 * @see useMemo - React hook for performance optimization
 */
export default function Sidebar(props) {
    /**
     * @constant
     * @description Background color for selected menu items.
     * Provides visual indication of the currently active menu item.
     *
     * @type {string}
     */
    const selectedMenuColor = '#f5e0b5';

    /**
     * @constant
     * @description Icon color for menu items.
     * Ensures consistent icon styling across all menu items.
     *
     * @type {string}
     */
    const iconColor = '#e0eeff';

    /**
     * @constant
     * @description Admin user data from Redux store.
     * Contains user permissions, role, and other authentication information.
     *
     * @type {Object}
     */
    const adminData = useSelector((state) => state.Auth.adminData);

    /**
     * @constant
     * @description React Router navigation hook.
     * Used for programmatic navigation between routes.
     *
     * @type {Function}
     */
    const navigate = useNavigate();

    /**
     * @state
     * @description Currently active submenu ID.
     * Tracks which submenu is currently expanded/active based on the current route.
     *
     * @type {number|null}
     */
    const [currentSubMenuId, setCurrentSubmenuId] = useState(null);

    /**
     * @constant
     * @description Memoized processed menu list with permission-based filtering.
     * Optimizes performance by caching the menu processing results and only recalculating
     * when dependencies change.
     *
     * Processing Steps:
     * 1. Maps through MenuList to check permissions for each menu item
     * 2. Determines visibility based on user permissions and role
     * 3. Filters out non-visible menu items
     * 4. Returns processed menu list ready for rendering
     *
     * Permission Logic:
     * - Finds matching permission in adminData.permissions for each menu
     * - Checks canView permission for the module
     * - Special handling for 'Admin' menu (hidden for SUB_ADMIN role)
     * - Defaults to visible if no specific permission is defined
     *
     * @type {Array<Object>}
     */
    const processedMenuList = useMemo(() => {
        if (!adminData) return [];
        const permissions = adminData.permissions || [];
        return MenuList?.map((menu) => {
            const hasPermission = permissions.find(
                (permission) => permission.moduleName === menu?.permissionName
            );
            const canView = hasPermission?.canView;

            // Return a new object with the calculated isShow property
            return {
                ...menu,
                isShow: menu.name === 'Admin' && adminData?.role === 'SUB_ADMIN' ? false : canView
            };
        })
            .map((menu) => ({
                ...menu,
                isShow: menu.isShow === undefined ? true : menu.isShow // Default to true if undefined
            }))
            .filter((menu) => menu.isShow); // Filter out non-visible menus
    }, [MenuList, adminData]);

    /**
     * @effect
     * @description Sets the current submenu ID based on the current route path.
     * Automatically determines which submenu should be active by matching the current
     * location pathname with submenu paths.
     *
     * Logic:
     * - Iterates through processedMenuList to find matching submenu
     * - Checks if any submenu path matches the current location.pathname
     * - Sets the index of the matching menu as currentSubMenuId
     * - Updates when processedMenuList or navigate changes
     *
     * @param {Array} processedMenuList - Processed menu list with permissions
     * @param {Function} navigate - React Router navigate function
     */
    useEffect(() => {
        setCurrentSubmenuId(
            processedMenuList.findIndex((menu) =>
                menu.submenu?.some((sub) => sub.path === location.pathname)
            )
        );
    }, [processedMenuList, navigate]); // Re-run when processedMenuList or navigate changes

    return (
        <Box
            className="sidebarMain"
            sx={{
                display: 'flex',
                paddingTop: '10px',
                background: '#002047',
                height: 'calc(100vh - 64px)',
                overflowX: 'hidden',
                overflowY: 'auto',
                position: 'sticky',
                top: '64px',
                left: 0,
                width: '100%',
                maxWidth: '280px'
            }}>
            <CssBaseline />
            <List>
                {processedMenuList?.map((menu, index) => (
                    <MenuCard
                        open={props.open}
                        currentSubMenuId={currentSubMenuId}
                        iconColor={iconColor}
                        index={index}
                        menus={menu}
                        navigate={navigate}
                        selectedMenuColor={selectedMenuColor}
                        setCurrentSubmenuId={setCurrentSubmenuId}
                        key={menu.name}
                        permissions={menu.other}
                    />
                ))}
            </List>
            <Divider />
        </Box>
    );
}
